// Boulders card (store-editor Products page polish, Jake direct ask, no
// card -- polish/UX carve-out): Cards vs Table view preference.
//
// Jake's decision: per-USER, ALL stores, via localStorage. Pick Table once
// and every store's Products page opens in Table on this browser. Keyed by
// userId (not per-store) -- mirrors the repo's canonical last-viewed-tab
// pattern (StoreProducts/Editor/productEditorTabRoutes.js:
// LAST_TAB_STORAGE_KEY, safeStorage() guard, validated read with a safe
// fallback) rather than inventing a new persistence shape.

export const VIEW_MODE_STORAGE_KEY_PREFIX = "tf:products-view-mode:";
export const DEFAULT_VIEW_MODE = "cards";
const VALID_MODES = new Set(["cards", "table"]);

// Guard localStorage access: it throws in private-mode Safari and is absent
// in non-browser (SSR / some test) environments. A failed read/write must
// never break the page -- it just falls back to the default view.
function safeStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function keyFor(userId) {
  return `${VIEW_MODE_STORAGE_KEY_PREFIX}${userId}`;
}

/**
 * Persist the user's view-mode choice. Ignored for a missing userId or an
 * invalid mode so we never store garbage that the reader would then have
 * to re-validate.
 */
export function persistViewModePreference(userId, mode) {
  if (userId == null || userId === "") return;
  if (!VALID_MODES.has(mode)) return;
  const store = safeStorage();
  if (!store) return;
  try {
    store.setItem(keyFor(userId), mode);
  } catch {
    // Quota / private-mode: silently skip -- persistence is best-effort.
  }
}

/**
 * Read the user's view-mode choice, validated. Returns DEFAULT_VIEW_MODE
 * when nothing valid is stored (missing userId, no value, corrupt value,
 * or a localStorage access failure) so the caller always has a safe mode
 * to render.
 */
export function readViewModePreference(userId) {
  if (userId == null || userId === "") return DEFAULT_VIEW_MODE;
  const store = safeStorage();
  if (!store) return DEFAULT_VIEW_MODE;
  let stored = null;
  try {
    stored = store.getItem(keyFor(userId));
  } catch {
    stored = null;
  }
  return VALID_MODES.has(stored) ? stored : DEFAULT_VIEW_MODE;
}
